import {useNavigate} from "react-router";
import React, {useEffect, useMemo, useState} from "react";
import {keepPreviousData, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {DefaultError} from "@tanstack/query-core";
import {enqueueSnackbar} from "notistack";
import {Button, LinearProgress, Stack} from "@mui/material";
import {TextInput} from "../form/TextInput";
import {Link} from "react-router-dom";
import {User} from "../../types/User";
import {createUser, loadUser, updateUser} from "../../service/user.service";
import {Switch} from "../form/Switch";
import {FormikProps, useFormik} from "formik";
import * as Yup from "yup";
import {UserPermissionsControl} from "./UserPermissionsControl";
import { useAuthStore } from "../../store/auth.store";
import { DateTime } from "../form/DateTime";

interface UserEditFormProps {
    id: string,
    parent: string,
    hideBack: boolean
}


export function UserEditForm({id, parent, hideBack}: UserEditFormProps) {
    const isNew = id === 'new';
    const navigate = useNavigate();
    const queryClient = useQueryClient()
    const authStore = useAuthStore();
    const [submitHappen, setSubmitHappen] = useState(false);
    const [repeatPassword, setRepeatPassword] = useState('');

    const {isLoading, error} = useQuery({
        queryKey: ['proposals', 'single', id],
        refetchOnWindowFocus: false,
        enabled: !isNew,
        queryFn: () => loadUser(id!).then(response => {
            formik.setValues(response.data);
            return response.data;
        }),
        placeholderData: keepPreviousData
    });

    const validationSchema = useMemo(() => {
        const obj: { login: Yup.StringSchema, password?: Yup.StringSchema } = {
            login: Yup.string()
                .min(4, "Login must be at least 4 characters")
                .required("Login is required"),
            password: Yup.string().test(
                'password',
                (e) => {
                    if(isNew) {
                        if(!e.value) {
                            return 'Password is required';
                        } else if(e.value.length < 8) {
                            return 'Should be at least 8 characters in length';
                        }
                    } else {
                        if(e.value && e.value.length < 8) {
                            return 'Should be at least 8 characters in length';
                        } else {
                            return undefined;
                        }
                    }
                },
                function (value) {
                    if (isNew) {
                        return !!value && value.length >= 8
                    } else {
                        return !value || (value === repeatPassword && value.length >= 8);
                    }
                }),
        };
        return Yup.object(obj);
    }, [isNew, repeatPassword])

    const formik: FormikProps<User> = useFormik<User>({
        initialValues: {} as User,
        validationSchema,
        onSubmit: (values) => {
            updateMutation.mutate(values)
        },
    });

    function cleanupUser(user:User) {
        //@ts-ignore
        delete user.authorities;
        return user;
    }

    const updateMutation = useMutation<User, DefaultError, User>({
        mutationFn: (payload) => (!isNew
                ? updateUser(cleanupUser(payload))
                : createUser(cleanupUser(payload))
        ).then(r => r.data),
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: ["users"]}).then(() => {
                navigate(parent);
            }).catch((e) => {
                enqueueSnackbar({message: "Failed to invalidate users cache " + e.message, variant: 'error'})
            })
        },
        onError: (error) => {
            enqueueSnackbar({message: "Failed to persist user " + error.message, variant: 'error'})
        }
    });

    useEffect(() => {
        if (!error) {
            return;
        }
        enqueueSnackbar({message: "Failed to load user: " + error.message, variant: 'error'})
    }, [error])

    const canManageUsers = authStore.can('manage_users');


    return <form style={{maxWidth: 800, margin: '0 auto'}} onSubmit={(e) => {
        setSubmitHappen(true);
        formik.handleSubmit(e)
    }}>
        {isLoading && <LinearProgress/>}
        <Stack flexDirection={"column"} gap={"15px"} className={"mb-15"}>
            <TextInput disabled={!isNew}
                       submitHappen={submitHappen}
                       formik={formik}
                       name={"login"}
                       label={"Login"}/>
            <TextInput disabled={!canManageUsers}
                       submitHappen={submitHappen}
                       formik={formik}
                       name={"phone"}
                       label={"Phone"}/>
            <TextInput formik={formik}
                       submitHappen={submitHappen}
                       type={"password"}
                       name={"password"}
                       label={"Password"}/>
            <TextInput value={repeatPassword}
                       onChange={(e) => setRepeatPassword(e.target.value)}
                       type={"password"}
                       name={"password"}
                       error={(formik.values.password || repeatPassword) && repeatPassword !== formik.values.password && "Password does not match"}
                       label={"Repeat Password"}/>

            <TextInput label={"User Name"}
                       name={"username"}
                       formik={formik}/>

            {canManageUsers && <Switch label={"User enabled"}
                    name={"enabled"}
                    formik={formik}/>}

            {canManageUsers && <Switch label={"User Verified"}
                    name={"verified"}
                    formik={formik}/>}
        </Stack>

        <UserPermissionsControl formik={formik}/>

        <Stack flexDirection={"row"} gap={"20px"}>
            {!hideBack && <Button color="inherit" variant={"contained"} component={Link} to={parent}>Back</Button>}
            <Button type="submit" variant="contained" color="primary">Submit</Button>
        </Stack>
    </form>
}